/**
 * Turning opaque upload failures into a real, queryable reason.
 *
 * The browser's fetch rejects with a bare `TypeError` for a whole family of
 * unrelated causes — DNS failure, an ISP/firewall block, a rejected CORS
 * preflight, a request aborted by navigation, and (the one that catches
 * everyone out) a Blob body whose backing file is no longer readable. Chrome
 * words all of these "Failed to fetch", Safari says "Load failed", and the AWS
 * SDK's fetch handler re-throws some as "network error". Three different
 * strings for a dozen different problems, none of which say what went wrong.
 *
 * That is why an upload failure spike is unactionable by default: every event
 * carries `TypeError` + one of three interchangeable strings. Nothing in the
 * error itself distinguishes "this user's wifi died" from "this user picked
 * cloud-only photos that were never materialised on the device" from "our
 * bucket's CORS policy is wrong" — but those want three completely different
 * fixes, and only one of them is worth retrying.
 *
 * So we stop relying on the error text and go and measure instead:
 *
 *  - `classifyUploadError` reads the structured fields the SDK *does* attach
 *    ($metadata.httpStatusCode, $fault, name/code) and only falls back to text
 *    matching when there is nothing better.
 *  - `probeUploadReachability` distinguishes "offline" from "AWS specifically
 *    unreachable" from "everything is fine, so it was not the network" by
 *    issuing `mode: 'no-cors'` requests. That mode is the trick: CORS cannot
 *    fail a no-cors request, so a rejection means a genuine transport failure
 *    and a resolution (even an opaque one) means the host answered.
 *  - `isFileStillReadable` re-reads a byte of the source file, which is the
 *    only way to catch a stale picker handle — the file's reported `size` is
 *    metadata captured at pick time and stays non-zero long after the bytes
 *    stop being reachable.
 *
 * Every field produced here is flat and primitive so it can go straight into a
 * PostHog property bag and be grouped on.
 */

/** Which network call actually failed. A bare TypeError never tells you. */
export type UploadPhase =
  | 'credentials'
  | 'read-file'
  | 'simple-upload'
  | 'create-multipart'
  | 'upload-part'
  | 'complete-multipart'
  | 'abort-multipart'
  | 'verify';

/** The actionable cause, as opposed to the error's own wording. */
export type UploadFailureKind =
  | 'offline'
  | 'aws-unreachable'
  | 'request-blocked'
  | 'credentials'
  | 'access-denied'
  | 'not-found'
  | 'throttled'
  | 'server-error'
  | 'timeout'
  | 'aborted'
  | 'file-unreadable'
  | 'verification'
  | 'unknown';

/**
 * Who or what stopped the upload, in words a report can group on without
 * reading it as "the app broke". `kind` is precise and stays the field for
 * diagnosis; `cause` is the coarse one a digest should count by:
 *
 *   network        the connection dropped, was too weak, or timed out
 *   cloud_file     the photo only exists in the user's cloud library (Google
 *                  Photos etc.) and the device could not hand over its bytes
 *   browser_blocked  something between the page and S3 refused the request
 *                  (content blocker, in-app browser, VPN)
 *   session        credentials or permission were refused
 *   service        AWS or our backend answered with an error or throttled
 *   cancelled      the request was aborted on purpose
 *   unconfirmed    bytes were sent but could not be confirmed afterwards
 *   unknown        nothing in the evidence says which of the above
 *
 * Only `unknown` is a genuine "we do not know why".
 */
export type UploadFailureCause =
  | 'network'
  | 'cloud_file'
  | 'browser_blocked'
  | 'session'
  | 'service'
  | 'cancelled'
  | 'unconfirmed'
  | 'unknown';

export function uploadFailureCause(kind: UploadFailureKind | 'invalid-file' | string | undefined): UploadFailureCause {
  switch (kind) {
    case 'offline':
    case 'aws-unreachable':
    case 'timeout':
      return 'network';
    case 'file-unreadable':
      return 'cloud_file';
    case 'request-blocked':
      return 'browser_blocked';
    case 'credentials':
    case 'access-denied':
      return 'session';
    case 'throttled':
    case 'server-error':
    case 'not-found':
      return 'service';
    case 'aborted':
      return 'cancelled';
    case 'verification':
      return 'unconfirmed';
    default:
      return 'unknown';
  }
}

export interface UploadFailureFacts {
  phase: UploadPhase;
  kind: UploadFailureKind;
  /** Whether trying the same thing again could plausibly work. */
  retryable: boolean;
  error_name?: string;
  error_message?: string;
  error_cause?: string;
  http_status?: number;
  aws_fault?: string;
  aws_request_id?: string;
  aws_extended_request_id?: string;
  /** How many times the SDK itself already retried before giving up on us. */
  sdk_attempts?: number;
  sdk_retry_delay_ms?: number;
  /**
   * Set when an "AWS unreachable" verdict was reached on a link too weak to
   * tell blocked from slow — see `refineWithReachability`.
   */
  weak_link?: boolean;
}

/**
 * Carries the phase alongside the original error instead of replacing it.
 *
 * Wrapping with `new Error(...)` — the usual reflex — destroys `$metadata`,
 * `$fault` and the AWS request id, which are the only fields worth having.
 * The original is kept on `.original` and stays the thing that gets classified.
 */
export class PhaseError extends Error {
  constructor(
    readonly phase: UploadPhase,
    readonly original: any
  ) {
    super(original?.message || String(original));
    this.name = original?.name || 'PhaseError';
    this.stack = original?.stack || this.stack;
  }
}

/** Attach a phase to whatever a block throws, without flattening it. */
export async function inPhase<T>(phase: UploadPhase, work: () => Promise<T>): Promise<T> {
  try {
    return await work();
  } catch (err) {
    throw err instanceof PhaseError ? err : new PhaseError(phase, err);
  }
}

const TRANSPORT_FAILURE_TEXT = /failed to fetch|network error|load failed|networkerror|connection|err_/i;

/**
 * Read whatever structure the error actually has.
 *
 * Deliberately checks fields before text: an SDK error with
 * `$metadata.httpStatusCode = 403` is unambiguous, whereas its `message` may
 * be anything from "Access Denied" to a raw XML fragment.
 */
export function classifyUploadError(error: any, phase: UploadPhase): UploadFailureFacts {
  const unwrapped = error instanceof PhaseError ? error.original : error;
  const actualPhase = error instanceof PhaseError ? error.phase : phase;

  const name: string | undefined = unwrapped?.name || unwrapped?.code || unwrapped?.Code;
  const message: string | undefined = unwrapped?.message ?? (unwrapped != null ? String(unwrapped) : undefined);
  const status: number | undefined = unwrapped?.$metadata?.httpStatusCode ?? unwrapped?.status;

  const facts: UploadFailureFacts = {
    phase: actualPhase,
    kind: 'unknown',
    retryable: true,
    error_name: name,
    error_message: message?.slice(0, 300),
    error_cause: unwrapped?.cause ? String(unwrapped.cause?.message || unwrapped.cause).slice(0, 300) : undefined,
    http_status: status,
    aws_fault: unwrapped?.$fault,
    aws_request_id: unwrapped?.$metadata?.requestId,
    aws_extended_request_id: unwrapped?.$metadata?.extendedRequestId,
    sdk_attempts: unwrapped?.$metadata?.attempts,
    sdk_retry_delay_ms: unwrapped?.$metadata?.totalRetryDelay,
  };

  // Our own verdicts first — these are conclusions, not symptoms, and must
  // never be reinterpreted as something retryable.
  if (name === 'UploadVerificationError') {
    return { ...facts, kind: 'verification', retryable: false };
  }
  if (name === 'TimeoutError' || /timed out after/i.test(message || '')) {
    return { ...facts, kind: 'timeout', retryable: true };
  }
  if (name === 'AbortError') {
    return { ...facts, kind: 'aborted', retryable: false };
  }
  if (name === 'NotReadableError' || /not readable|filereader error|file is empty/i.test(message || '')) {
    return { ...facts, kind: 'file-unreadable', retryable: false };
  }

  // A handful of AWS conditions arrive as 4xx but are genuinely transient. The
  // SDK's own retry layer used to absorb them silently; now that `maxAttempts`
  // is 1 (see createS3Client) nothing else will, so they have to be recognised
  // here — otherwise a clock skew or a mid-flight credential rotation gets
  // reported as a permanent "access denied" and the file is dropped.
  const RECOVERABLE_AWS_CODES =
    /RequestTimeTooSkewed|ExpiredToken|TokenRefreshRequired|InvalidAccessKeyId|RequestExpired|SlowDown|InternalError|ServiceUnavailable/i;
  if (name && RECOVERABLE_AWS_CODES.test(name)) {
    return {
      ...facts,
      kind: /SlowDown|ServiceUnavailable/i.test(name) ? 'throttled' : 'credentials',
      retryable: true,
    };
  }

  // Anything with an HTTP status got a real response, so the transport worked
  // and the text is irrelevant.
  if (typeof status === 'number') {
    if (status === 403) {
      // 403 during credential resolution is a rejected identity, not a bucket
      // policy problem — worth separating, they page different people.
      return { ...facts, kind: actualPhase === 'credentials' ? 'credentials' : 'access-denied', retryable: false };
    }
    if (status === 404) return { ...facts, kind: 'not-found', retryable: false };
    if (status === 429 || status === 503) return { ...facts, kind: 'throttled', retryable: true };
    if (status >= 500) return { ...facts, kind: 'server-error', retryable: true };
    if (status >= 400) return { ...facts, kind: 'access-denied', retryable: false };
  }

  if (/credential|token|identity|logins|NotAuthorized|ExpiredToken/i.test(`${name} ${message}`)) {
    return { ...facts, kind: 'credentials', retryable: true };
  }

  // A bare TypeError. This is the ambiguous case the probe exists to resolve;
  // left as 'unknown' here so the caller can decide whether the extra
  // round-trips are worth it.
  if (name === 'TypeError' || TRANSPORT_FAILURE_TEXT.test(message || '')) {
    return { ...facts, kind: 'unknown', retryable: true };
  }

  return facts;
}

/** True when the classification is the ambiguous transport failure. */
export function needsReachabilityProbe(facts: UploadFailureFacts): boolean {
  return facts.kind === 'unknown' && facts.error_name === 'TypeError';
}

export interface ReachabilityReport {
  navigator_online: boolean;
  s3_reachable: boolean;
  cognito_reachable: boolean;
  control_reachable: boolean;
  probe_ms: number;
}

/**
 * Ask the network directly rather than trusting `navigator.onLine`.
 *
 * `navigator.onLine` only reports whether a link exists — it is `true` on a
 * connected-but-dead wifi, on a captive portal, and on mobile data with no
 * route. Every observed upload failure had it set to `true`, so as a guard it
 * is close to useless; only its `false` is trustworthy.
 *
 * `mode: 'no-cors'` is what makes these probes meaningful. It removes CORS
 * from the equation entirely, so the result separates cleanly:
 *   - rejects  -> genuine transport failure (DNS, blocked, no route)
 *   - resolves -> host answered, so a CORS rejection on the real request means
 *                 the bucket policy is at fault rather than the network
 */
export async function probeUploadReachability(
  s3Origin: string,
  cognitoOrigin: string,
  controlOrigin: string,
  timeoutMs = 5000
): Promise<ReachabilityReport> {
  const startedAt = Date.now();

  const reach = async (url: string): Promise<boolean> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      // An opaque response is still proof the host answered; we never read it.
      await fetch(url, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  };

  const [s3, cognito, control] = await Promise.all([
    reach(s3Origin),
    reach(cognitoOrigin),
    reach(controlOrigin),
  ]);

  return {
    navigator_online: navigator.onLine,
    s3_reachable: s3,
    cognito_reachable: cognito,
    control_reachable: control,
    probe_ms: Date.now() - startedAt,
  };
}

/** A classification, plus the probe evidence when a probe was needed. */
export type DiagnosedFailure = UploadFailureFacts & Partial<ReachabilityReport>;

/** What the browser reports about the link, as far as it reports anything. */
export interface LinkQuality {
  effectiveType?: string;
  /** Round-trip estimate in ms. */
  rtt?: number;
  /** Downlink estimate in Mbps. */
  downlink?: number;
}

/**
 * True when the link is weak enough that a probe timing out says nothing about
 * whether AWS is blocked.
 *
 * The probe gives each host five seconds. On a 2g link with a 1.5 s round trip
 * a TLS handshake alone can eat that, so "unreachable" was being declared on
 * links that were merely slow — and declared non-retryable, which dropped the
 * file for good. Real logs: a 4g-labelled connection reporting 1.3 Mbps and a
 * 700 ms RTT, falling to 2g with S3 and Cognito "unreachable" while the control
 * host still answered, then answering again within the minute.
 */
export function isWeakLink(link: LinkQuality | undefined): boolean {
  if (!link) return false;
  if (link.effectiveType === 'slow-2g' || link.effectiveType === '2g' || link.effectiveType === '3g') return true;
  if (typeof link.rtt === 'number' && link.rtt >= 600) return true;
  if (typeof link.downlink === 'number' && link.downlink > 0 && link.downlink < 2) return true;
  return false;
}

/** A probe that used its whole budget did not get an answer — it ran out of time. */
export function probeTimedOut(report: ReachabilityReport, timeoutMs = 5000): boolean {
  return report.probe_ms >= timeoutMs - 100;
}

/** Fold a probe result back into a concrete cause. */
export function refineWithReachability(
  facts: UploadFailureFacts,
  report: ReachabilityReport,
  link?: LinkQuality,
  timeoutMs = 5000
): UploadFailureFacts {
  if (!report.control_reachable && !report.s3_reachable && !report.cognito_reachable) {
    return { ...facts, kind: 'offline', retryable: true };
  }
  if (!report.s3_reachable || !report.cognito_reachable) {
    // The rest of the internet answers but AWS does not. On a healthy link that
    // is ISP/DNS interception or a corporate filter, and retrying on this
    // connection will not help. On a weak link — or when the probe simply ran
    // out of time — it is just as likely a slow handshake, so the file waits
    // for the connection instead of being written off.
    const weak = isWeakLink(link) || probeTimedOut(report, timeoutMs);
    return { ...facts, kind: 'aws-unreachable', retryable: weak, weak_link: weak };
  }
  // Every host answered, yet the request itself still died at the transport
  // layer. Deliberately NOT called 'cors': a rejected preflight lands here, but
  // so does a request killed by an extension or a proxy, and so does a body
  // whose backing file became unreadable between the readability check and the
  // send. The honest statement is "the hosts are up and this request was
  // blocked" — calling it 'cors' would assert a cause the probe cannot prove.
  return { ...facts, kind: 'request-blocked', retryable: false };
}

/**
 * Whether the picked file can still be read.
 *
 * The case this catches: on Android/iOS a `File` from the picker is a handle,
 * not a copy. If the photo is cloud-only (Google Photos "free up space"), gets
 * moved, or the app is backgrounded long enough for the OS to revoke access,
 * the handle goes stale — but `file.size` keeps returning the number captured
 * at pick time, so every size check still passes. Handing that Blob to fetch
 * makes the *fetch* reject with `TypeError: Failed to fetch`, which is
 * indistinguishable from a network failure unless you check the file itself.
 *
 * Reads one byte, because the failure is about reachability of the bytes, not
 * how many there are.
 */
export async function isFileStillReadable(file: File): Promise<boolean> {
  return probeFileRead(file, 'head');
}

/**
 * Read one byte from the start or the end of the file.
 *
 * The picking-time check reads the head. The tail matters for diagnosis: if a
 * handle can answer for the first byte but not the last, "readable" at pick
 * time was only ever true of the part the probe touched.
 */
export async function probeFileRead(file: File, where: 'head' | 'tail' = 'head'): Promise<boolean> {
  try {
    const probe = where === 'tail'
      ? file.slice(Math.max(0, file.size - 1), file.size)
      : file.slice(0, 1);
    if (typeof (probe as any).arrayBuffer === 'function') {
      const buf = await (probe as any).arrayBuffer();
      return buf.byteLength > 0;
    }
    return await new Promise<boolean>(resolve => {
      const reader = new FileReader();
      reader.onload = () => resolve(!!reader.result && (reader.result as ArrayBuffer).byteLength > 0);
      reader.onerror = () => resolve(false);
      reader.onabort = () => resolve(false);
      reader.readAsArrayBuffer(probe);
    });
  } catch {
    return false;
  }
}

/**
 * Backoff that can actually outlast an outage.
 *
 * The previous schedule was 2s then 4s — six seconds total, which loses to any
 * interruption longer than a traffic light. Jitter matters because a batch
 * fails in lockstep: without it every file in flight retries on the same tick
 * and recreates the burst that caused the failure.
 */
export function retryDelayMs(attempt: number, kind: UploadFailureKind): number {
  const base = kind === 'throttled' ? 5000 : 2000;
  const capped = Math.min(base * Math.pow(2, attempt - 1), 30000);
  return Math.round(capped * (0.7 + Math.random() * 0.6));
}